/**
 * What this farmer grows, season by season. Several crops per season are the
 * rule here (soybean then wheat and garlic on different khasras), so this is
 * a set of chips per season plus a free box for a crop not on the list. It is
 * the one input the per-crop advisory cannot do without.
 */
import { useEffect, useState } from 'react';
import type { CropEntry, Season } from '@shared/model';
import { CROPS, SEASONS } from '@shared/model';

export function parseCrops(json: string | null | undefined): CropEntry[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json) as CropEntry[];
    return Array.isArray(v) ? v.filter((c) => c && typeof c.crop === 'string') : [];
  } catch {
    return [];
  }
}

export function CropPicker({ value, busy, onSave }: { value: CropEntry[]; busy: boolean; onSave: (crops: CropEntry[]) => void }): JSX.Element {
  const [crops, setCrops] = useState<CropEntry[]>(value);
  const [other, setOther] = useState<Record<Season, string>>({ kharif: '', rabi: '', zaid: '' });
  useEffect(() => setCrops(value), [value]);

  const has = (s: Season, c: string): boolean => crops.some((x) => x.season === s && x.crop === c);
  const toggle = (s: Season, c: string): void =>
    setCrops((cur) => (has(s, c) ? cur.filter((x) => !(x.season === s && x.crop === c)) : [...cur, { season: s, crop: c }]));
  const changed = JSON.stringify(crops) !== JSON.stringify(value);

  return (
    <div>
      {SEASONS.map((s) => (
        <div key={s.id} style={{ marginBottom: 8 }}>
          <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>{s.label} <span style={{ opacity: 0.7 }}>· {s.months}</span></div>
          <div className="chips">
            {[...CROPS[s.id], ...crops.filter((x) => x.season === s.id && !CROPS[s.id].includes(x.crop)).map((x) => x.crop)].map((c) => (
              <button key={c} type="button" className={`chip-toggle ${has(s.id, c) ? 'is-on' : ''}`} style={{ padding: '4px 9px', fontSize: 12 }} onClick={() => toggle(s.id, c)}>
                {c}
              </button>
            ))}
            <input
              className="chip-toggle"
              style={{ padding: '4px 9px', fontSize: 12, width: 110 }}
              placeholder="other crop…"
              value={other[s.id]}
              onChange={(e) => setOther({ ...other, [s.id]: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && other[s.id].trim()) {
                  toggle(s.id, other[s.id].trim());
                  setOther({ ...other, [s.id]: '' });
                }
              }}
            />
          </div>
        </div>
      ))}
      <div className="fc__buttons">
        <button type="button" className="btn-primary" disabled={busy || !changed} onClick={() => onSave(crops)}>Save crops</button>
        {crops.length ? <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>{crops.length} crop{crops.length === 1 ? '' : 's'} across {new Set(crops.map((c) => c.season)).size} season{new Set(crops.map((c) => c.season)).size === 1 ? '' : 's'}</span> : null}
      </div>
    </div>
  );
}
